import { type Editor, Node } from "@tiptap/core";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import { type NodeViewProps, ReactNodeViewRenderer } from "@tiptap/react";
import { collectRowCells, findCellAndRow } from "./row/row-utils";
import { EmailBlockDefinition } from "./types";

/**
 * Whether an attr still holds its schema default.
 *
 * Object-valued attrs (a block's `style` map) are compared by content, not by
 * identity: a document round-tripped through JSON rebuilds the empty map as a
 * fresh object, and treating that as a change would put a `data-attrs` payload
 * on every block that has no styles at all.
 */
function isDefaultValue(value: unknown, defaultValue: unknown): boolean {
  if (value === defaultValue) return true;
  if (value && defaultValue && typeof value === "object" && typeof defaultValue === "object") {
    return JSON.stringify(value) === JSON.stringify(defaultValue);
  }
  return false;
}

/**
 * Creates a Tiptap Node extension from an EmailBlockDefinition.
 * This bridges the block registry and Tiptap's extension system.
 */
export function createNodeExtension<T extends Record<string, unknown> = Record<string, unknown>>(definition: EmailBlockDefinition<T>, options?: {
  draggable?: boolean;
  mode?: "simple" | "block";
}) {
  const mode = options?.mode ?? "simple";
  return Node.create({
    name: definition.name,
    group: definition.schema.group ?? "emailBlock",
    content: definition.schema.content,
    draggable: options?.draggable ?? definition.schema.draggable ?? false,
    isolating: definition.schema.isolating ?? false,
    defining: true,
    addAttributes() {
      const attrs: Record<string, {
        default: unknown;
        parseHTML?: (element: HTMLElement) => unknown;
      }> = {};
      for (const [key, config] of Object.entries(definition.schema.attrs)) {
        const {
          default: defaultVal,
          jsonOnly
        } = config as {
          default: unknown;
          jsonOnly?: boolean;
        };
        // An attr with no HTML attribute form travels in `data-attrs` alone.
        // A `style` map would otherwise be stringified into — and merged with —
        // the element's own style attribute; markup stored verbatim would be
        // re-escaped into an attribute nothing reads back.
        const isMapAttr = jsonOnly || !!defaultVal && typeof defaultVal === "object";
        attrs[key] = {
          default: defaultVal,
          ...(isMapAttr ? {
            renderHTML: () => ({})
          } : {}),
          parseHTML: (element: HTMLElement) => {
            // Primary: read from data-attrs JSON (survives clipboard roundtrip)
            const raw = element.getAttribute("data-attrs");
            if (raw) {
              try {
                const parsed = JSON.parse(raw);
                if (key in parsed) return parsed[key];
              } catch {
                // Invalid JSON, fall through
              }
            }
            if (isMapAttr) return undefined;
            // Fallback: read from the HTML attribute — but only on our own
            // output. On foreign markup an attr name can collide with a real
            // HTML one (`<img width="120">` vs the block's `width: "100%"`
            // enum), and this fallback runs *after* a parse rule's getAttrs, so
            // it would silently overwrite the mapped value with an invalid one.
            if (!element.hasAttribute("data-block-type")) return undefined;
            const htmlVal = element.getAttribute(key);
            if (htmlVal !== null) return htmlVal;
            return undefined;
          }
        };
      }
      return attrs;
    },
    parseHTML() {
      // The data-block-type rule matches our own output (clipboard round-trip);
      // the definition's rules match markup authored elsewhere. Ours is listed
      // first and at a higher priority so composer content never falls through
      // to the looser tag-based rules.
      return [{
        tag: `div[data-block-type="${definition.name}"]`,
        priority: 100
      }, ...(definition.parseHTML?.() ?? [])];
    },
    renderHTML({
      node,
      HTMLAttributes
    }) {
      // Encode non-default attrs as JSON for clipboard roundtrip safety.
      // Browsers may strip non-standard HTML attributes from clipboard data,
      // but data-* attributes are always preserved.
      const nonDefaultAttrs: Record<string, unknown> = {};
      for (const [key, config] of Object.entries(definition.schema.attrs)) {
        const defaultVal = (config as {
          default: unknown;
        }).default;
        if (!isDefaultValue(node.attrs[key], defaultVal)) {
          nonDefaultAttrs[key] = node.attrs[key];
        }
      }
      const htmlAttrs: Record<string, unknown> = {
        ...HTMLAttributes,
        "data-block-type": definition.name
      };
      if (Object.keys(nonDefaultAttrs).length > 0) {
        htmlAttrs["data-attrs"] = JSON.stringify(nonDefaultAttrs);
      }

      // Leaf nodes (no content) must not include the content hole (0)
      if (!definition.schema.content) {
        return ["div", htmlAttrs];
      }
      return ["div", htmlAttrs, 0];
    },
    addKeyboardShortcuts() {
      const blockName = definition.name;
      const isNonParagraphContent = definition.name !== "emailParagraph" && definition.schema.content === "inline*";
      const isParagraph = definition.name === "emailParagraph";
      const isIsolating = definition.schema.isolating ?? false;

      /** Check if cursor is inside this isolating block (at any depth). */
      const isInsideThisBlock = (editor: Editor) => {
        if (!isIsolating) return false;
        const {
          $from
        } = editor.state.selection;
        for (let d = $from.depth; d >= 0; d--) {
          if ($from.node(d).type.name === blockName) return true;
        }
        return false;
      };

      /** Escape down: jump to next row or create a new empty one. */
      const escapeDown = (editor: Editor) => {
        const {
          $from
        } = editor.state.selection;
        const context = findCellAndRow($from);
        if (!context) return false;
        const {
          rowDepth
        } = context;
        const afterRow = $from.after(rowDepth);
        return editor.chain().command(({
          tr,
          dispatch
        }) => {
          if (!dispatch) return true;
          if (afterRow < tr.doc.content.size) {
            tr.setSelection(TextSelection.near(tr.doc.resolve(afterRow), 1));
            return true;
          }
          const rowType = editor.schema.nodes["emailRow"];
          const cellType = editor.schema.nodes["emailCell"];
          const paragraphType = editor.schema.nodes["emailParagraph"];
          if (!rowType || !cellType || !paragraphType) return false;
          const newRow = rowType.create(null, [cellType.create({
            colSpan: 4
          }, [paragraphType.create()])]);
          tr.insert(afterRow, newRow);
          tr.setSelection(TextSelection.near(tr.doc.resolve(afterRow + 3)));
          return true;
        }).run();
      };
      return {
        Escape: ({
          editor
        }) => {
          // In simple mode there are no row-level NodeSelections, so Escape
          // should not convert TextSelection → NodeSelection.
          if (mode === "simple") return false;
          const {
            selection
          } = editor.state;

          // If in TextSelection inside a block, convert to NodeSelection on the block
          if (selection instanceof TextSelection) {
            const {
              $from
            } = selection;
            for (let d = $from.depth; d >= 0; d--) {
              if ($from.node(d).type.name === blockName) {
                const blockPos = $from.before(d);
                editor.view.dispatch(editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, blockPos)));
                return true;
              }
            }
          }

          // If already NodeSelection, deselect (handled by default behavior)
          return false;
        },
        ArrowDown: ({
          editor
        }) => {
          if (!isInsideThisBlock(editor)) return false;
          return escapeDown(editor);
        },
        ArrowRight: ({
          editor
        }) => {
          if (!isInsideThisBlock(editor)) return false;
          // Only escape when at end of the block's text
          const {
            $from,
            empty
          } = editor.state.selection;
          if (!empty || $from.parentOffset !== $from.parent.content.size) return false;
          return escapeDown(editor);
        },
        ArrowUp: ({
          editor
        }) => {
          if (!isInsideThisBlock(editor)) return false;
          const {
            $from
          } = editor.state.selection;
          const context = findCellAndRow($from);
          if (!context) return false;
          const {
            rowDepth
          } = context;
          const beforeRow = $from.before(rowDepth);
          if (beforeRow <= 0) return false;
          return editor.chain().command(({
            tr,
            dispatch
          }) => {
            if (!dispatch) return true;
            tr.setSelection(TextSelection.near(tr.doc.resolve(beforeRow), -1));
            return true;
          }).run();
        },
        ArrowLeft: ({
          editor
        }) => {
          if (!isInsideThisBlock(editor)) return false;
          // Only escape when at start of the block's text
          const {
            $from,
            empty
          } = editor.state.selection;
          if (!empty || $from.parentOffset !== 0) return false;
          const context = findCellAndRow($from);
          if (!context) return false;
          const {
            rowDepth
          } = context;
          const beforeRow = $from.before(rowDepth);
          if (beforeRow <= 0) return false;
          return editor.chain().command(({
            tr,
            dispatch
          }) => {
            if (!dispatch) return true;
            tr.setSelection(TextSelection.near(tr.doc.resolve(beforeRow), -1));
            return true;
          }).run();
        },
        Backspace: ({
          editor
        }) => {
          if (!isParagraph) return false;
          const {
            $from,
            empty
          } = editor.state.selection;
          if (!empty || $from.parentOffset !== 0) return false;
          if ($from.parent.type.name !== "emailParagraph") return false;
          if ($from.parent.content.size !== 0) return false;
          const context = findCellAndRow($from);
          if (!context) return false;
          const {
            cellDepth,
            rowDepth,
            cellNode,
            rowNode
          } = context;

          // Current cell must be empty (single empty paragraph)
          if (cellNode.childCount !== 1) return false;

          // Must be in the first block of the cell
          if ($from.index(cellDepth) !== 0) return false;

          // If row has multiple cells, delete just this cell
          if (rowNode.childCount > 1) {
            return editor.chain().command(({
              tr,
              dispatch
            }) => {
              if (!dispatch) return true;
              const cellPos = $from.before(cellDepth);
              const cellColSpan = cellNode.attrs.colSpan as number;
              const cellIdx = $from.index(rowDepth);
              const cells = collectRowCells(rowNode, $from.start(rowDepth));

              // Give space to previous cell, or next if first
              const neighborIdx = cellIdx > 0 ? cellIdx - 1 : cellIdx + 1;
              const neighbor = cells[neighborIdx];
              const neighborNewSpan = (neighbor.node.attrs.colSpan as number) + cellColSpan;

              // Delete the empty cell
              tr.delete(cellPos, cellPos + cellNode.nodeSize);

              // Update neighbor's colSpan (position may have shifted)
              const mappedNeighborPos = tr.mapping.map(neighbor.pos);
              tr.setNodeMarkup(mappedNeighborPos, undefined, {
                ...neighbor.node.attrs,
                colSpan: neighborNewSpan
              });

              // Place cursor in the neighbor
              const $neighbor = tr.doc.resolve(mappedNeighborPos + 1);
              tr.setSelection(TextSelection.near($neighbor, 1));
              return true;
            }).run();
          }

          // Single-cell row: delete the entire row
          const parentNode = $from.node(rowDepth - 1);
          if (!parentNode || parentNode.childCount <= 1) return false;
          return editor.chain().command(({
            tr,
            dispatch
          }) => {
            if (!dispatch) return true;
            const rowPos = $from.before(rowDepth);
            tr.setSelection(NodeSelection.create(tr.doc, rowPos));
            tr.deleteSelection();
            return true;
          }).run();
        },
        Enter: ({
          editor
        }) => {
          // Isolating blocks (e.g. button): Enter creates a new row below.
          if (isIsolating) {
            const {
              $from
            } = editor.state.selection;
            if ($from.parent.type.name !== blockName) return false;
            const context = findCellAndRow($from);
            if (!context) return false;
            const {
              rowDepth
            } = context;
            return editor.chain().command(({
              tr,
              dispatch
            }) => {
              if (!dispatch) return true;
              const rowType = editor.schema.nodes["emailRow"];
              const cellType = editor.schema.nodes["emailCell"];
              const paragraphType = editor.schema.nodes["emailParagraph"];
              if (!rowType || !cellType || !paragraphType) return false;
              const afterRow = $from.after(rowDepth);
              const newRow = rowType.create(null, [cellType.create({
                colSpan: 4
              }, [paragraphType.create()])]);
              tr.insert(afterRow, newRow);
              tr.setSelection(TextSelection.near(tr.doc.resolve(afterRow + 3)));
              return true;
            }).run();
          }

          // Non-paragraph blocks (heading, blockquote): create paragraph
          // below instead of splitting into another block of the same type.
          if (isNonParagraphContent) {
            const {
              $from,
              empty
            } = editor.state.selection;
            if ($from.parent.type.name !== blockName) return false;
            if (!empty || $from.parentOffset !== $from.parent.content.size) {
              return false;
            }
            return editor.chain().command(({
              tr,
              dispatch
            }) => {
              if (!dispatch) return true;
              const paragraphType = editor.schema.nodes["emailParagraph"];
              if (!paragraphType) return false;
              const pos = $from.after();
              tr.insert(pos, paragraphType.create());
              tr.setSelection(TextSelection.near(tr.doc.resolve(pos + 1)));
              return true;
            }).run();
          }

          // Empty paragraph in a full-width single-cell row: create a new
          // row below instead of splitting within the cell.
          if (isParagraph) {
            const {
              $from,
              empty
            } = editor.state.selection;
            if ($from.parent.type.name !== "emailParagraph") return false;
            if (!empty || $from.parent.content.size !== 0) return false;

            // Walk up to find the cell and row
            const cellDepth = $from.depth - 1; // cell is parent of paragraph
            const rowDepth = cellDepth - 1; // row is parent of cell
            if (cellDepth < 1 || rowDepth < 0) return false;
            const cellNode = $from.node(cellDepth);
            const rowNode = $from.node(rowDepth);
            if (cellNode?.type.name !== "emailCell" || rowNode?.type.name !== "emailRow") return false;

            // Only for full-width single-cell rows where this is the last block
            if (rowNode.childCount !== 1 || cellNode.attrs.colSpan !== 4) return false;
            const isLastChild = $from.index(cellDepth) === cellNode.childCount - 1;
            if (!isLastChild) return false;
            return editor.chain().command(({
              tr,
              dispatch
            }) => {
              if (!dispatch) return true;
              const rowType = editor.schema.nodes["emailRow"];
              const cellType = editor.schema.nodes["emailCell"];
              const paragraphType = editor.schema.nodes["emailParagraph"];
              if (!rowType || !cellType || !paragraphType) return false;
              const rowEnd = $from.after(rowDepth);
              const newRow = rowType.create(null, [cellType.create({
                colSpan: 4
              }, [paragraphType.create()])]);
              if (cellNode.childCount > 1) {
                // Delete the empty paragraph before inserting the new row
                const paragraphPos = $from.before();
                const paragraphEnd = $from.after();
                tr.delete(paragraphPos, paragraphEnd);
                const offset = paragraphEnd - paragraphPos;
                tr.insert(rowEnd - offset, newRow);
                tr.setSelection(TextSelection.near(tr.doc.resolve(rowEnd - offset + 3)));
              } else {
                // Only paragraph in the cell — keep it to avoid
                // leaving the cell empty (schema requires 1+ children).
                tr.insert(rowEnd, newRow);
                tr.setSelection(TextSelection.near(tr.doc.resolve(rowEnd + 3)));
              }
              return true;
            }).run();
          }
          return false;
        }
      };
    },
    addNodeView() {
      return ReactNodeViewRenderer((props: NodeViewProps) => {
        const Component = definition.tiptapComponent;
        // ProseMirror attrs are typed as Record<string, any> but will
        // match T at runtime since they're populated from our schema.
        return <Component attrs={props.node.attrs as T} updateAttrs={props.updateAttributes as (attrs: Partial<T>) => void} selected={props.selected} />;
      }, {
        // Let mousedown events pass through to ProseMirror so the
        // node-selection-drag plugin can handle block selection.
        // By default, ReactNodeViewRenderer stops events outside the
        // contentDOM (e.g. clicks on NodeViewWrapper padding), which
        // prevents our plugin from seeing them.
        stopEvent: ({
          event
        }) => {
          // A field inside a block (an image link box) handles its own
          // clicks and keys; the editor would otherwise take focus away.
          if ((event.target as HTMLElement).closest?.("[data-editor-field]")) {
            return true;
          }
          if (event.type === "mousedown") return false;
          // Stop other events outside contentDOM (default behavior)
          return true;
        }
      });
    }
  });
}